import { useEffect } from "react";
import styled from "styled-components";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import { API_DEPLOYMENTS, API_USERS } from "../../utils/apis";
import { CAN_DEPLOY_USERS } from "../../utils/constants";
import {
  addSelectItem,
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
  userSelectItem,
} from "../../utils/helpers";
import { URL_DEPLOYMENTS, URL_USERS } from "../../utils/urls";
import useGet from "../common/useGet";
import useSearchOptions from "../common/useSearchOptions";
import { auditFields, userFullName } from "../assets/assetUtils";
import {
  ACCOUNT_TYPES,
  AGENT_GROUP,
  ENFORCEMENT_GROUP,
  deploymentAccountType,
  getDeploymentGroup,
  useAvailableTeamOptions,
  useDeploymentPermissions,
  useEnforcementTeamOptions,
  useParkingYardOptions,
  useParkingYardSearch,
} from "./deploymentUtils";

const yardNames = (ids: string[] = [], names: Record<string, string>) =>
  ids.map((yard) => names[yard] ?? yard).join(", ");

const assignment = (
  item: any,
  yards: Record<string, string>,
  teams: Record<string, string>,
) =>
  item?.enforcement_team
    ? teams[item.enforcement_team] ?? item.enforcement_team
    : yardNames(item?.parking_yards, yards);

export const useDeployments = () => {
  const can = useDeploymentPermissions();
  const canDeploy = can(CAN_DEPLOY_USERS);
  const yards = useParkingYardOptions();
  const teams = useEnforcementTeamOptions();

  const tableColumns = () => [
    { id: "user", header: "User" },
    { id: "account_type", header: "Account Type" },
    {
      id: "parking_yards",
      header: "Parking Yards",
      filter: { type: "select", options: yards.options },
    },
    {
      id: "enforcement_team",
      header: "Enforcement Team",
      filter: { type: "select", options: teams.options },
    },
    { id: "deployed_by", header: "Deployed By" },
    { id: "created_at", header: "Deployed On" },
    { id: "updated_at", header: "Last Updated" },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      ...auditFields(item),
      user: userFullName(item?.user_details),
      account_type: deploymentAccountType(item),
      parking_yards: yardNames(item?.parking_yards, yards.names),
      enforcement_team: item?.enforcement_team
        ? teams.names[item.enforcement_team] ?? item.enforcement_team
        : null,
      deployed_by: userFullName(item?.deployed_by),
      actions: (
        <FormActions
          actions={createViewEdit(URL_DEPLOYMENTS, item?.id, canDeploy)}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Deployed User",
        fields: {
          name: userFullName(item?.user_details),
          account_type: deploymentAccountType(item),
        },
      },
      {
        title: "Deployment",
        fields: {
          [item?.enforcement_team ? "enforcement_team" : "parking_yards"]:
            assignment(item, yards.names, teams.names),
          deployed_by: userFullName(item?.deployed_by),
          deployed_on: formatDateTime(item?.created_at),
        },
      },
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Deployments",
    tableColumns,
    tableData,
    viewData,
    formRoute: canDeploy ? getForm(URL_DEPLOYMENTS) : undefined,
    createTitle: "Deploy User",
    activityFilter: "deployments",
  };
};

export const useDeploymentsForm = ({
  id,
  form,
  updateFormValue,
  updateFormValues,
  setError,
  searchParams,
}: TFormHookParams & { searchParams?: Record<string, string> } = {}) => {
  const userId = form?.user;
  const { loadData: loadUser, data: userData } = useGet();
  const { loadData: loadExisting, data: existingData } = useGet();
  const yards = useParkingYardOptions();
  const availableTeams = useAvailableTeamOptions();
  const allTeams = useEnforcementTeamOptions();
  const yardSearch = useParkingYardSearch(form?.parking_yards, yards.names);

  // preselect the user when opened from the users register
  useEffect(() => {
    if (!id && searchParams?.user) updateFormValue?.("user", searchParams.user);
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadUser({ id: userId, api: API_USERS });
    if (!id) {
      updateFormValues?.({ parking_yards: undefined, enforcement_team: null });
      loadExisting({ api: API_DEPLOYMENTS, params: { user: userId } });
    }
  }, [userId]);

  const user = userData?.id === userId ? userData : null;
  const lockedUser = !!id || !!searchParams?.user;
  const existing = id
    ? null
    : (existingData?.results || []).find((d: any) => d?.user === userId);

  useEffect(() => {
    if (existing) {
      updateFormValues?.({
        parking_yards: existing.parking_yards,
        enforcement_team: existing.enforcement_team,
      });
    }
  }, [existing?.id]);

  const users = useSearchOptions({
    api: API_USERS,
    searchParam: "first_name",
    toOption: userSelectItem,
    selected: user ? userSelectItem(user) : undefined,
    enabled: !id,
  });

  const group = getDeploymentGroup(user);
  const isAgent = group === AGENT_GROUP;
  const isEnforcement = group === ENFORCEMENT_GROUP;

  // the officer's current team stays selectable even when it is full
  const currentTeam = form?.enforcement_team;
  const teamOptions =
    currentTeam &&
    !availableTeams.options.some((t: any) => t.value === currentTeam)
      ? [
          addSelectItem({
            value: currentTeam,
            label: allTeams.names[currentTeam] ?? "Current team",
          }),
          ...availableTeams.options,
        ]
      : availableTeams.options;

  const blocker = !user
    ? null
    : user?.is_active === false
      ? "This user is deactivated and cannot be deployed."
      : !group
        ? "Only Agents and Enforcement Officers can be deployed. Check this user's role."
        : null;

  const sections: IFormSection[] = [
    {
      title: "Deployment Details",
      fields: [
        {
          field_name: "user",
          label: "User",
          type: "select",
          search: users,
          props: {
            required: !id,
            disabled: lockedUser,
            placeholder: "Search user by first name",
          },
        },
        {
          field_name: "user_summary",
          type: "component",
          visible: !!user,
          component: (
            <div className="font12">
              <p className="mb-1">
                <strong>Account Type:</strong>{" "}
                {group ? ACCOUNT_TYPES[group] : "Unknown"}
              </p>
              {existing && (
                <p className="mb-1">
                  This user is already deployed. Submitting will update their
                  current deployment.
                </p>
              )}
              {blocker && (
                <p className="mb-0" style={{ color: "red" }}>
                  {blocker}
                </p>
              )}
            </div>
          ),
        },
        {
          field_name: "parking_yards",
          label: "Parking Yards",
          type: "select",
          visible: isAgent,
          search: yardSearch,
          props: {
            required: isAgent && !id,
            multiple: true,
            placeholder: "Search parking yards by name",
          },
        },
        {
          field_name: "enforcement_team",
          label: "Enforcement Group",
          type: "select",
          visible: isEnforcement,
          options_data: teamOptions,
          loading: availableTeams.loading,
          props: {
            required: isEnforcement && !id,
            placeholder: "Select enforcement group",
          },
        },
      ],
    },
  ];

  // Only send what the user's group allows, and only what changed
  const assignmentParams = (payload: any, current?: any) => {
    if (isAgent && payload?.parking_yards !== undefined) {
      const same =
        current &&
        [...(payload.parking_yards || [])].sort().join() ===
          [...(current.parking_yards || [])].sort().join();
      return same ? {} : { parking_yards: payload.parking_yards };
    }
    if (isEnforcement && payload?.enforcement_team !== undefined) {
      return current?.enforcement_team === payload.enforcement_team
        ? {}
        : { enforcement_team: payload.enforcement_team };
    }
    return {};
  };

  const onSubmit = (payload: any) => {
    if (id) {
      return { id, api: API_DEPLOYMENTS, params: assignmentParams(payload) };
    }

    if (existing) {
      const params = assignmentParams(payload, existing);
      if (!Object.keys(params).length) {
        setError?.({
          [isAgent ? "parking_yards" : "enforcement_team"]:
            "The user is already deployed here.",
        });
        return;
      }
      return { id: existing.id, api: API_DEPLOYMENTS, params };
    }

    return {
      api: API_DEPLOYMENTS,
      params: { user: payload?.user, ...assignmentParams(payload) },
    };
  };

  const preview = () => (
    <PreviewTable>
      <tbody>
        <tr>
          <th>User</th>
          <td>{userFullName(user)}</td>
        </tr>
        <tr>
          <th>Account Type</th>
          <td>{group ? ACCOUNT_TYPES[group] : "Unknown"}</td>
        </tr>
        {isAgent && (
          <tr>
            <th>Parking Yards</th>
            <td>{yardNames(form?.parking_yards, yards.names)}</td>
          </tr>
        )}
        {isEnforcement && (
          <tr>
            <th>Enforcement Group</th>
            <td>
              {allTeams.names[form?.enforcement_team] ?? form?.enforcement_team}
            </td>
          </tr>
        )}
      </tbody>
      {(id || existing) && (
        <caption>This replaces the user's current deployment.</caption>
      )}
    </PreviewTable>
  );

  return {
    sections,
    title: "Deploy User",
    redirect: getRegister(id ? URL_DEPLOYMENTS : URL_USERS),
    onSubmit,
    preview,
    submitDisabled: !!blocker || (!!userId && !user),
  };
};

const PreviewTable = styled.table`
  width: 100%;
  margin-top: 12px;
  caption-side: bottom;

  th,
  td {
    padding: 6px 0;
    vertical-align: top;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    width: 40%;
    color: #6b7280;
    font-weight: 500;
  }

  caption {
    padding-top: 10px;
    color: #d97706;
    font-size: 0.8125rem;
  }
`;
